import * as React from 'react'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Skeleton } from '@/components/ui/skeleton'
import { AssessmentForm } from './AssessmentForm'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useInvitations } from '../hooks/useInvitations'
import { useAssessments } from '@/features/assessments/hooks/useAssessments'
import { RoleType, AssessmentStatus, AssessmentType, Decision } from '@promo/shared'
import { AssessmentItem } from './AssessmentsSection'
import { AssessmentDecisionChart } from '@/components/custom/AssessmentDecisionChart'
import type { SelectOption } from '@/components/ui/editable-select'
import { parseNameFromEmail } from '@promo/shared'

interface UnblindedAssessmentsSectionProps {
  packetId: string
  readOnly?: boolean
  isEditor?: boolean
  canViewAssessments?: boolean
}

export function UnblindedAssessmentsSection({ packetId, readOnly = false, isEditor = false, canViewAssessments = true }: UnblindedAssessmentsSectionProps) {
  const { session } = useAuth()
  const currentUserId = session.user?.id ?? ''

  // Manager/SeniorLeader override: selected assessor
  const [selectedAssessorId, setSelectedAssessorId] = React.useState<string>('')

  // Invitations
  const { data: assessorInvitations = [] } = useInvitations(
    packetId,
    RoleType.ASSESSOR,
    { enabled: isEditor }
  )
  const invitedCount = assessorInvitations.length

  // Fetch assessments
  const {
    blinded,
    unblinded,
    createAssessment,
    updateAssessment,
    deleteAssessment,
    isLoading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
  } = useAssessments(packetId, canViewAssessments)

  const assessmentAssessorCount = React.useMemo(
    () => new Set([...blinded, ...unblinded].map(a => a.assessorId)).size,
    [blinded, unblinded]
  )
  const expectedAssessorCount = invitedCount || assessmentAssessorCount

  // Build dropdown options for managers (all invites, excluding those who already have assessments)
  const availableAssessors: SelectOption[] = assessorInvitations
    .filter(inv => !unblinded.some(a => a.assessorId === inv.inviteeId))
    .map(inv => ({
      value: inv.inviteeId,
      label: parseNameFromEmail(inv.inviteeEmail) || inv.inviteeEmail
    }))

  const completedBlind = blinded.filter(a => a.status === AssessmentStatus.COMPLETED).length
  const completedUnblind = unblinded.filter(a => a.status === AssessmentStatus.COMPLETED).length
  const userUnblind = unblinded.find(a => a.assessorId === currentUserId)
  const [newUnblindKey, setNewUnblindKey] = React.useState(0)

  // Calculate promote/defer counts for the unblinded phase
  const promoteCountUnblinded = unblinded
    .filter(a => a.status === AssessmentStatus.COMPLETED && a.decision === Decision.PROMOTE)
    .length;
  const deferCountUnblinded = unblinded
    .filter(a => a.status === AssessmentStatus.COMPLETED && a.decision === Decision.DEFER)
    .length;

  const blindedPhaseComplete =
    expectedAssessorCount > 0 && completedBlind >= expectedAssessorCount;
  // Assessor-facing reads can still have unblinded rows even when invitation and blinded
  // rows are unavailable, so infer phase availability only when those sources are absent.
  const inferredUnblindedPhaseActive =
    invitedCount === 0 && completedBlind === 0 && completedUnblind > 0;
  const unblindedPhaseActive = blindedPhaseComplete || inferredUnblindedPhaseActive;
  const showUnblindedChart =
    unblindedPhaseActive && expectedAssessorCount > 0 && completedUnblind >= expectedAssessorCount;

  const isEditable = !readOnly

  if (isLoading) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Unblinded Assessments</h2>
        <div className="flex items-center justify-center py-8">
          <Spinner className="h-8 w-8" />
          <span className="ml-2">Loading assessments...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Unblinded Assessments</h2>
        <div className="bg-red-50 text-red-800 p-4 rounded-md mt-4">
          <h3 className="text-lg font-semibold">Error loading assessments</h3>
          <p>{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <section className={`mb-6 space-y-6 ${!unblindedPhaseActive ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex justify-between lg:justify-start gap-2 items-center mb-6">
        <h2 className="text-xl font-semibold">Unblinded Assessments</h2>
        <Badge>{completedUnblind} of {expectedAssessorCount}</Badge>
      </div>

      {/* Assessment Decision Chart for Unblinded Phase */}
      {showUnblindedChart && (
        <div className="my-6 flex justify-center">
          <AssessmentDecisionChart 
            promoteCount={promoteCountUnblinded}
            deferCount={deferCountUnblinded}
          />
        </div>
      )}

      <div>
        <p className="mb-4">
          You <strong>cannot change your decision</strong> once you have submitted your assessment.
        </p>
      </div>

      {!unblindedPhaseActive ? (
        <div className="text-sm text-muted-foreground">
          Unblinded assessments are locked until all blinded assessments complete.
        </div>
      ) : unblinded.length === 0 ? (
        <Skeleton className="h-4 w-3/4" />
      ) : (
        unblinded.map(item => (
          <AssessmentItem
            key={item.id}
            item={item}
            currentUserId={currentUserId}
            isEditable={isEditable && item.assessorId === currentUserId}
            isManager={isEditor}
            updateAssessment={updateAssessment}
            deleteAssessment={deleteAssessment}
            isUpdating={isUpdating}
            isDeleting={isDeleting}
          />
        ))
      )}

      {/* New unblinded assessment form */}
      {isEditable && completedBlind >= invitedCount && (
        (!isEditor && !userUnblind) || 
        (isEditor && availableAssessors.length > 0)
      ) && (
        <div className="py-6 space-y-4 border-b last:border-b-0">
          <AssessmentForm
            key={newUnblindKey}
            onSubmit={(data) =>
              createAssessment({ 
                ...data, 
                packetId: packetId, 
                type: AssessmentType.UNBLINDED,
                ...(isEditor ? { assessorId: selectedAssessorId } : {})
              }, { onSuccess: () => setNewUnblindKey(k => k + 1) })
            }
            onCancel={() => setNewUnblindKey(k => k + 1)}
            isSubmitting={isCreating}
            isManager={isEditor}
            availableAssessors={availableAssessors}
            selectedAssessorId={selectedAssessorId}
            onAssessorChange={setSelectedAssessorId}
          />
        </div>
      )}
    </section>
  )
}
